import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { Checkbox, FormControlLabel, CheckboxProps as MuiCheckboxProps } from '@mui/material';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * AigleCheckbox Props
 */
export interface AigleCheckboxProps extends Omit<MuiCheckboxProps, 'onChange'> {
  /** Label 텍스트 */
  label?: string;
  /** 변경 이벤트 핸들러 */
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  /** 체크 아이콘 (커스텀) */
  checkedIcon?: React.ReactNode;
  /** 언체크 아이콘 (커스텀) */
  uncheckedIcon?: React.ReactNode;
  /** Indeterminate 아이콘 (커스텀) */
  indeterminateIcon?: React.ReactNode;
  /** 에러 상태 여부 */
  error?: boolean;
  /** 컨테이너 추가 스타일 */
  containerStyle?: SerializedStyles;
  /** 리플 효과 비활성화 */
  disableRipple?: boolean;
}

// Styled Components
const StyledFormControlLabel = styled(FormControlLabel)<{
  $error?: boolean;
  $containerStyle?: SerializedStyles;
}>`
  margin: 0;
  display: flex;
  align-items: center;
  user-select: none;
  
  .MuiCheckbox-root {
    padding: 0 8px 0 0;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }

  .MuiTypography-root {
    font-size: 14px;
    color: ${aigleColor.text.secondary};
    line-height: 1.5;
  }
  
  ${({ $error }) => $error && css`
    .MuiCheckbox-root {
      color: ${aigleColor.semantic.error};
    }
  `}
  
  &.Mui-disabled {
    .MuiTypography-root {
      color: ${aigleColor.text.disabled};
    }
  }
  
  ${({ $containerStyle }) => $containerStyle}
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: ${aigleColor.border.default};
    transition: all 0.2s ease;
    
    &:hover {
      background-color: ${aigleColor.alpha.black.alpha5};
    }
    
    &.Mui-checked {
      color: ${aigleColor.brand.primary};
    }
    
    &.Mui-disabled {
      color: ${aigleColor.text.disabled};
      opacity: 0.6;
    }
  }
`;

// 기본 체크 아이콘 (체크됨)
const DefaultCheckedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="4" fill={aigleColor.brand.primary}/>
    <path
      d="M7 12L10 15L17 8"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 기본 체크 아이콘 (체크 안됨)
const DefaultUncheckedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="3.5"
      y="3.5"
      width="17"
      height="17"
      rx="3.5"
      fill="white"
      stroke={aigleColor.border.default}
    />
    <path
      d="M7 12L10 15L17 8"
      stroke={aigleColor.grey.light}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Indeterminate 아이콘 (부분 선택)
const DefaultIndeterminateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="4" fill={aigleColor.brand.primary}/>
    <path
      d="M8 12H16"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * AigleCheckbox Component
 * 
 * MUI Checkbox를 기반으로 한 일관된 디자인의 Checkbox 컴포넌트
 * 리플 효과와 애니메이션으로 더 나은 인터랙션을 제공합니다.
 * 
 * @example
 * // Basic Checkbox
 * <AigleCheckbox label="동의합니다" />
 * 
 * @example
 * // Controlled Checkbox
 * const [checked, setChecked] = useState(false);
 * <AigleCheckbox 
 *   label="로그인 저장"
 *   checked={checked}
 *   onChange={(checked) => setChecked(checked)}
 * />
 * 
 * @example
 * // Custom Icons
 * <AigleCheckbox 
 *   label="약관 동의"
 *   checkedIcon={<CustomCheckedIcon />}
 *   uncheckedIcon={<CustomUncheckedIcon />}
 * />
 */
export const AigleCheckbox = React.forwardRef<HTMLButtonElement, AigleCheckboxProps>(
  (
    {
      label,
      checked,
      defaultChecked,
      onChange,
      checkedIcon,
      uncheckedIcon,
      indeterminateIcon,
      indeterminate,
      disabled = false,
      error = false,
      containerStyle,
      disableRipple = false,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = event.target.checked;
      onChange?.(newChecked, event);
    };
    
    const checkboxControl = (
      <StyledCheckbox
        ref={ref}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={handleChange}
        disabled={disabled}
        disableRipple={disableRipple}
        indeterminate={indeterminate}
        icon={uncheckedIcon || <DefaultUncheckedIcon />}
        checkedIcon={checkedIcon || <DefaultCheckedIcon />}
        indeterminateIcon={indeterminateIcon || <DefaultIndeterminateIcon />}
        {...props}
      />
    );
    
    // label이 없으면 Checkbox만 반환
    if (!label) {
      return checkboxControl;
    }
    
    return (
      <StyledFormControlLabel
        control={checkboxControl}
        label={label}
        disabled={disabled}
        $error={error}
        $containerStyle={containerStyle}
      />
    );
  }
);

AigleCheckbox.displayName = 'AigleCheckbox';

export default AigleCheckbox;

